import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPodcast, faMicrophone, faHeadphones } from '@fortawesome/free-solid-svg-icons';
import { Header, Footer, VideoCard, VideoCardSkeleton } from './shared';
import { useYouTubeVideos } from '../hooks/useFetch';
import { YOUTUBE_CONFIG } from '../config/constants';
import { useDevice } from '../hooks/useMediaQuery';
import type { YouTubeVideo } from '../types';

const Podcasts: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPodcast, setSelectedPodcast] = useState<YouTubeVideo | null>(null);
  useDevice(); // For future responsive enhancements

  const { videos, loading, error } = useYouTubeVideos(YOUTUBE_CONFIG.podcastChannelId);

  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim()) return videos;
    return videos.filter((video: YouTubeVideo) =>
      video.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [videos, searchTerm]);

  const handlePlayPodcast = (video: YouTubeVideo) => {
    setSelectedPodcast(video);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-dark-950">
      <Header
        onSearch={setSearchTerm}
        searchValue={searchTerm}
        showSearch={true}
      />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-dark-950 to-cyan-600/20" />
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 40%, rgba(99, 102, 241, 0.2) 0%, transparent 50%),
                             radial-gradient(circle at 80% 60%, rgba(6, 182, 212, 0.2) 0%, transparent 50%)`,
          }} />
          {/* Sound waves animation */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="absolute border-2 border-indigo-400 rounded-full animate-ping"
                style={{
                  width: `${i * 100}px`,
                  height: `${i * 100}px`,
                  left: `${-i * 50}px`,
                  top: `${-i * 50}px`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: '3s',
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full
              bg-indigo-500/20 border border-indigo-500/30 mb-6 animate-fade-in">
              <FontAwesomeIcon icon={faMicrophone} className="text-indigo-400" />
              <span className="text-indigo-300 font-medium">The Good Company KE</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-4
              animate-fade-in" style={{ animationDelay: '100ms' }}>
              <span className="bg-gradient-to-r from-indigo-400 via-cyan-400 to-teal-400 
                bg-clip-text text-transparent">
                Podcasts
              </span>
            </h1>
            <p className="text-gray-400 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto 
              animate-fade-in" style={{ animationDelay: '200ms' }}>
              Listen to thought-provoking conversations and stories
            </p>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 sm:gap-12 lg:gap-16 
            text-center animate-fade-in" style={{ animationDelay: '300ms' }}>
            <div>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-bold 
                bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                {videos.length}
              </p>
              <p className="text-gray-500 text-sm sm:text-base">Episodes</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-bold 
                bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                HD
              </p>
              <p className="text-gray-500 text-sm sm:text-base">Video Quality</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-bold 
                bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                New
              </p>
              <p className="text-gray-500 text-sm sm:text-base">Weekly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Selected Podcast Player */}
      {selectedPodcast && (
        <section className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 
          mb-12 animate-fade-in">
          <div className="relative rounded-2xl overflow-hidden 
            bg-dark-900 shadow-2xl shadow-indigo-500/10">
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${selectedPodcast.id}?autoplay=1`}
                title={selectedPodcast.title}
                className="w-full h-full"
                allowFullScreen
                allow="autoplay; encrypted-media; fullscreen"
              />
            </div>
            <div className="p-4 sm:p-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2">{selectedPodcast.title}</h2>
                <div className="flex items-center gap-2 text-gray-400">
                  <FontAwesomeIcon icon={faHeadphones} className="text-indigo-400" />
                  <span>Now Playing</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedPodcast(null)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20
                  transition-colors text-sm font-medium"
              >
                Close Player
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 pb-20">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
            {searchTerm ? (
              <>
                Results for <span className="text-indigo-400">"{searchTerm}"</span>
              </>
            ) : (
              <>All <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 
                bg-clip-text text-transparent">Episodes</span></>
            )}
          </h2>
          {filteredVideos.length > 0 && (
            <span className="text-gray-500">
              {filteredVideos.length} episode{filteredVideos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="video-grid">
            <VideoCardSkeleton count={12} />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-400 text-xl mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-indigo-500 rounded-full font-semibold
                hover:bg-indigo-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Podcasts Grid */}
        {!loading && !error && (
          <>
            {filteredVideos.length > 0 ? (
              <div className="video-grid">
                {filteredVideos.map((video, index) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    index={index}
                    type="podcast"
                    onPlay={handlePlayPodcast}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <FontAwesomeIcon icon={faPodcast} className="text-6xl text-gray-700 mb-4" />
                <p className="text-gray-400 text-xl mb-4">
                  {searchTerm
                    ? `No podcasts found for "${searchTerm}"`
                    : 'No podcast episodes available'
                  }
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-indigo-400 hover:text-indigo-300 font-semibold"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Podcasts;
